import { useState } from "react";
import axios from "axios";
import { Heart, CreditCard } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const amounts = [10, 25, 50, 100, 250, 500];

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [loading, setLoading] = useState(false);
  const success = searchParams.get("success");

  const handleDonate = async () => {
    const amount = selectedAmount || parseFloat(customAmount);
    if (!amount || amount < 1) {
      toast.error("Please select or enter a donation amount.");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/donate`, { amount, origin_url: origin });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div data-testid="donate-page">
        <section className="bg-[#1C2E4A] py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Thank You</p>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white">God Bless You</h1>
          </div>
        </section>
        <section className="py-20 md:py-28 bg-white">
          <div className="max-w-lg mx-auto px-6 text-center">
            <Heart className="w-12 h-12 text-[#C8A97E] mx-auto mb-4" />
            <h2 className="font-serif text-2xl font-semibold text-[#1C2E4A] mb-3">Thank You for Your Generosity</h2>
            <p className="text-sm text-[#595959]">Your tithe and donation supports our church ministry and helps us serve families through faith and education.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div data-testid="donate-page">
      <section className="bg-[#1C2E4A] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A97E] font-semibold mb-3">Give</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-3xl">
            Tithes & Donations
          </h1>
          <p className="mt-6 text-base text-white/70 max-w-2xl">
            Your generosity supports our ministry and helps us serve families through faith and education.
          </p>
        </div>
      </section>
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-lg mx-auto px-6 md:px-12">
          <div className="bg-[#FAF9F6] rounded-xl border border-[#E5E2DC] p-8">
            <h2 className="font-serif text-xl font-semibold text-[#1C2E4A] mb-6 text-center">Select an Amount</h2>
            <div className="grid grid-cols-3 gap-3 mb-6">
              {amounts.map((amt) => (
                <button
                  key={amt}
                  data-testid={`donate-amount-${amt}`}
                  onClick={() => { setSelectedAmount(amt); setCustomAmount(""); }}
                  className={`py-3 rounded-lg font-medium text-sm transition-all border-2 ${
                    selectedAmount === amt
                      ? "border-[#C8A97E] bg-[#C8A97E]/10 text-[#1C2E4A]"
                      : "border-[#E5E2DC] text-[#595959] hover:border-[#C8A97E]/50"
                  }`}
                >
                  ${amt}
                </button>
              ))}
            </div>
            <div className="mb-6">
              <Label className="text-[#1A1A1A] text-sm font-medium mb-1.5 block">Custom Amount</Label>
              <Input
                data-testid="donate-custom-input"
                type="number"
                min="1"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => { setCustomAmount(e.target.value); setSelectedAmount(null); }}
                className="border-[#E5E2DC] bg-white focus:border-[#1C2E4A] focus:ring-1 focus:ring-[#1C2E4A]"
              />
            </div>
            <button
              data-testid="donate-submit-btn"
              onClick={handleDonate}
              disabled={loading}
              className="w-full bg-[#C8A97E] text-white hover:bg-[#B3956A] px-6 py-3 rounded-md transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <CreditCard className="w-4 h-4" />
              {loading ? "Processing..." : `Donate ${selectedAmount ? `$${selectedAmount}` : customAmount ? `$${customAmount}` : ""}`}
            </button>
            <p className="text-xs text-[#595959]/60 text-center mt-4">Secure payment processed through Stripe.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
